<template>
  <div>
    <s-header>
      <div class="mt-3 -mb-3">
        <div class="">Resources</div>
        <div v-if="isAdmin || isDeveloper" class="flex space-x-2 mt-3">
          <button
            v-for="method in sortMethods"
            :key="method.value"
            @click="setSortMethod(method.value)"
            class="px-3 py-1 text-sm rounded-sm transition-colors duration-150"
            :class="
              sortMethod === method.value
                ? 'bg-gray-800 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            "
          >
            {{ method.label }}
          </button>
        </div>
      </div>
    </s-header>

    <section class="mt-10">
      <!-- Sequential or Alphabetical sorting (no categories) -->
      <template v-if="sortMethod !== 'categorical'">
        <div
          v-for="(resource, index) in resourcesSorted"
          :key="resource.id"
          class="mt-4 px-6 py-2 border-l-4 border-gray-800 hover:bg-gray-50 transition-colors duration-150"
        >
          <a
            :href="resource.url"
            target="_blank"
            class="flex flex-col items-start font-degular text-2xl sm:text-3xl font-semibold text-gray-800"
          >
            {{ resource.name }}
            <span
              v-if="resource.description"
              class="text-xl sm:text-2xl font-normal text-gray-600 leading-none"
            >
              {{ resource.description }}
            </span>
          </a>
        </div>
      </template>

      <!-- Categorical sorting -->
      <template v-else>
        <div v-for="category in categories" :key="category">
          <h3
            class="mt-8 mb-4 text-xl sm:text-2xl uppercase font-bold text-gray-600"
          >
            {{ category }}
          </h3>
          <div
            v-for="resource in getResourcesByCategory(category)"
            :key="resource.id"
            class="mt-4 px-6 py-2 border-l-4 border-gray-800 hover:bg-gray-50 transition-colors duration-150"
          >
            <a
              :href="resource.url"
              target="_blank"
              class="flex flex-col items-start font-degular text-2xl sm:text-3xl font-semibold text-gray-800"
            >
              {{ resource.name }}
              <span
                v-if="resource.description"
                class="text-xl sm:text-2xl font-normal text-gray-600 leading-none"
              >
                {{ resource.description }}
              </span>
            </a>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import SHeader from '@/components/SHeader'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'ResourceList',
  data: function () {
    return {
      activeChild: -1,
      sortMethods: [
        { label: 'Categorical', value: 'categorical' },
        { label: 'Alphabetical', value: 'alphabetical' }
        // { label: 'Sequential', value: 'sequential' }
      ]
    }
  },
  components: {
    SHeader
  },
  computed: {
    ...mapGetters('user', ['isManager', 'isStaff', 'isAdmin', 'isDeveloper']),
    ...mapGetters('global', ['resources']),
    ...mapGetters('config', ['resourceSortMethod']),

    sortMethod() {
      return this.resourceSortMethod || 'categorical'
    },

    resourcesSorted() {
      try {
        if (!this.resources) return []

        const resourceArray = Object.values(this.resources)

        // Apply sorting based on selected method
        if (this.sortMethod === 'alphabetical') {
          return resourceArray.sort((a, b) => a.name.localeCompare(b.name))
        } else {
          // Sequential is default (original order)
          return resourceArray.sort((a, b) => a.id.localeCompare(b.id))
        }
      } catch {
        return []
      }
    },

    categories() {
      if (!this.resources) return []

      // Get resources in sequential order
      const sequentialResources = Object.values(this.resources).sort((a, b) =>
        a.id.localeCompare(b.id)
      )

      // Track categories in order of first appearance
      const orderedCategories = []
      const seenCategories = new Set()

      // Add categories in the order they first appear
      sequentialResources.forEach((resource) => {
        const category = resource.category || 'Uncategorized'
        if (!seenCategories.has(category)) {
          seenCategories.add(category)
          orderedCategories.push(category)
        }
      })

      return orderedCategories
    }
  },
  methods: {
    ...mapActions('config', ['setResourceSortMethod']),

    setSortMethod(method) {
      this.setResourceSortMethod(method)
    },

    getResourcesByCategory(category) {
      if (!this.resources) return []

      return Object.values(this.resources)
        .filter((resource) => {
          if (category === 'Uncategorized') {
            return !resource.category
          }
          return resource.category === category
        })
        .sort((a, b) => a.id.localeCompare(b.id))
    }
  }
}
</script>
